import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { getPageName } from '../../utils/pageNameMapper';
import { getGuestDisplayName } from '../../utils/guestDisplayUtils';
import io from 'socket.io-client';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * ActiveUserTracker - Tracks user activity and sends heartbeats to server
 * Handles idle detection and page navigation tracking
 */
const ActiveUserTracker = () => {
  const location = useLocation();
  const { user, isAuthenticated, deviceId } = useAuthContext();
  
  const socketRef = useRef(null);
  const sessionIdRef = useRef(null);
  const lastActivityTimeRef = useRef(Date.now());
  const idleCheckIntervalRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const pageStartTimeRef = useRef(Date.now());
  const sessionStartTimeRef = useRef(Date.now()); // Track total session time
  const previousUrlRef = useRef(null);

  // Use refs for frequently changing values to avoid recreating callbacks
  const idleTimeRef = useRef(0);
  const statusRef = useRef('active');
  
  const [, setIdleTime] = useState(0);
  const [, setStatus] = useState('active');

  // Generate or retrieve session ID
  const getSessionId = useCallback(() => {
    if (!sessionIdRef.current) {
      let sessionId = sessionStorage.getItem('active_user_session_id');
      if (!sessionId) {
        sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('active_user_session_id', sessionId);
      }
      sessionIdRef.current = sessionId;
    }
    return sessionIdRef.current;
  }, []);

  // Get user info for tracking
  const getUserInfo = useCallback(() => {
    if (isAuthenticated && user) {
      return {
        user_id: user.id,
        user_type: user.user_type || 'registered',
        // Prefer chosen guest label (fname) so it matches profile Name
        username:
          user.user_type === 'guest'
            ? getGuestDisplayName(user)
            : user.email || `${user.fname || ''} ${user.lname || ''}`.trim() || 'User'
      };
    } else {
      // Check if there's a guest name in localStorage
      const guestName = localStorage.getItem('guest_name') || `Guest_${deviceId?.substr(-6) || 'unknown'}`;
      return {
        user_id: null,
        user_type: 'anonymous',
        username: guestName
      };
    }
  }, [isAuthenticated, user, deviceId]);

  // Update last activity time
  const updateActivity = useCallback(() => {
    lastActivityTimeRef.current = Date.now();
    // Use ref to check current status without dependency
    if (statusRef.current !== 'active') {
      statusRef.current = 'active';
      idleTimeRef.current = 0;
      setStatus('active');
      setIdleTime(0);
    }
  }, []); // No dependencies - stable callback

  // Calculate idle time and status
  const calculateIdleStatus = useCallback(() => {
    const now = Date.now();
    const idleSeconds = Math.floor((now - lastActivityTimeRef.current) / 1000);
    
    // Update refs first
    idleTimeRef.current = idleSeconds;
    
    let newStatus = 'active';
    if (idleSeconds < 15) {
      newStatus = 'active';
    } else if (idleSeconds < 60) {
      newStatus = 'idle';
    } else {
      newStatus = 'away';
    }
    
    // Only update state if status changed (to avoid unnecessary re-renders)
    if (statusRef.current !== newStatus) {
      statusRef.current = newStatus;
      setStatus(newStatus);
    }
    
    // Always update idleTime state for UI (but use ref for heartbeat)
    setIdleTime(idleSeconds);
  }, []); // No dependencies - stable callback

  // Send heartbeat to server
  // Use refs for frequently changing values to keep callback stable
  const sendHeartbeat = useCallback(() => {
    if (!socketRef.current || !socketRef.current.connected) {
      return;
    }

    // Calculate current idle status before sending
    calculateIdleStatus();

    const sessionId = getSessionId();
    const userInfo = getUserInfo();
    const currentUrl = location.pathname;
    const currentPageName = getPageName(currentUrl);
    const timeOnPage = Math.floor((Date.now() - pageStartTimeRef.current) / 1000);
    const totalSessionTime = Math.floor((Date.now() - sessionStartTimeRef.current) / 1000); // Total time on website

    // Use refs for current values (they're always up-to-date)
    const heartbeatData = {
      session_id: sessionId,
      user_id: userInfo.user_id,
      user_type: userInfo.user_type,
      username: userInfo.username,
      device_id: deviceId,
      current_url: currentUrl,
      current_page_name: currentPageName,
      idle_time: idleTimeRef.current, // Use ref instead of state
      status: statusRef.current, // Use ref instead of state
      last_activity_timestamp: new Date(lastActivityTimeRef.current).toISOString(),
      time_on_current_page: timeOnPage,
      total_session_time: totalSessionTime // Total time spent on website
    };

    socketRef.current.emit('active-user:heartbeat', heartbeatData);
  }, [location.pathname, getSessionId, getUserInfo, deviceId, calculateIdleStatus]); // Removed idleTime and status from deps

  const forceSyncGenRef = useRef(0);
  const syncPersistedForce = useCallback(async () => {
    const gen = ++forceSyncGenRef.current;
    try {
      const res = await fetch(`${API_BASE}/api/settings/public/mym-titles-config`);
      const data = await res.json().catch(() => ({}));
      if (gen !== forceSyncGenRef.current) return;
      const forced =
        Array.isArray(data.config) && data.config.find((c) => c.forceModal);
      if (!forced?.id) {
        window.dispatchEvent(new Event('mym-force-clear'));
        return;
      }
      const path = (window.location.pathname || '').toLowerCase();
      const params = new URLSearchParams(window.location.search || '');
      if (
        path === '/mindyourmusic' &&
        String(params.get('forceId')) === String(forced.id)
      ) {
        return;
      }
      window.dispatchEvent(
        new CustomEvent('mym-force-open', { detail: { id: forced.id } })
      );
    } catch (e) {
      console.warn('[ActiveUserTracker] MYM persisted force sync failed', e);
    }
  }, []);
  const syncPersistedForceRef = useRef(syncPersistedForce);
  syncPersistedForceRef.current = syncPersistedForce;
  const sendHeartbeatRef = useRef(sendHeartbeat);
  sendHeartbeatRef.current = sendHeartbeat;

  // Setup activity listeners
  useEffect(() => {
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ];

    // Throttle activity updates to once per second
    let throttleTimeout = null;
    const throttledUpdateActivity = () => {
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          updateActivity();
          throttleTimeout = null;
        }, 1000);
      }
    };

    activityEvents.forEach(event => {
      window.addEventListener(event, throttledUpdateActivity, { passive: true });
    });

    // Handle window focus/blur
    const handleFocus = () => updateActivity();
    const handleBlur = () => {
      // Don't update on blur, let idle time accumulate
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, throttledUpdateActivity);
      });
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [updateActivity]);

  // Setup Socket.IO connection (once only — do not depend on sendHeartbeat to avoid reconnect loop)
  useEffect(() => {
    const socket = io(API_BASE, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[ActiveUserTracker] Connected to server');
      sendHeartbeatRef.current?.();
      syncPersistedForceRef.current?.();
    });

    socket.on('disconnect', () => {
      console.log('[ActiveUserTracker] Disconnected from server');
    });

    socket.on('connect_error', (error) => {
      console.warn('[ActiveUserTracker] Connection error:', error);
    });

    // Listen for buzzer.force_open and broadcast a DOM event
    socket.on('buzzer.force_open', () => {
      window.dispatchEvent(new Event('buzzer-force-open'));
    });

    // Listen for MYM force open and broadcast a DOM event with payload
    socket.on('mym.force_open', (payload) => {
      try {
        const detail = payload && typeof payload === 'object' ? payload : {};
        window.dispatchEvent(
          new CustomEvent('mym-force-open', { detail })
        );
      } catch (e) {
        console.warn('[ActiveUserTracker] Failed to dispatch mym-force-open event', e);
      }
    });

    // Listen for MYM force clear and broadcast a DOM event
    socket.on('mym.force_clear', () => {
      try {
        window.dispatchEvent(new Event('mym-force-clear'));
      } catch (e) {
        console.warn('[ActiveUserTracker] Failed to dispatch mym-force-clear event', e);
      }
    });

    // Listen for quiz force question (single question push to logged-in users)
    socket.on('quiz:force_question', (payload) => {
      try {
        const detail = payload && typeof payload === 'object' ? payload : {};
        window.dispatchEvent(new CustomEvent('quiz-force-question', { detail }));
      } catch (e) {
        console.warn('[ActiveUserTracker] Failed to dispatch quiz-force-question event', e);
      }
    });

    return () => {
      if (socketRef.current) {
        // Send disconnect event
        const sessionId = getSessionId();
        socketRef.current.emit('active-user:disconnect', { session_id: sessionId });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [getSessionId]);

  useEffect(() => {
    syncPersistedForceRef.current?.();
  }, [isAuthenticated, user?.id]);

  // Setup idle check interval (every second)
  useEffect(() => {
    idleCheckIntervalRef.current = setInterval(() => {
      calculateIdleStatus();
    }, 1000);

    return () => {
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
      }
    };
  }, [calculateIdleStatus]);

  // Setup heartbeat interval (every 30 seconds) — use ref so interval isn't recreated when sendHeartbeat identity changes
  useEffect(() => {
    heartbeatIntervalRef.current = setInterval(() => {
      sendHeartbeatRef.current?.();
    }, 30000); // 30 seconds

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, []);

  // Handle page navigation
  useEffect(() => {
    const currentUrl = location.pathname;
    
    // If URL changed, reset page start time
    if (previousUrlRef.current !== currentUrl) {
      if (previousUrlRef.current !== null) {
        // Page changed - send final heartbeat for previous page
        sendHeartbeatRef.current?.();
      }
      
      previousUrlRef.current = currentUrl;
      pageStartTimeRef.current = Date.now();
      
      // Send heartbeat for new page immediately
      setTimeout(() => {
        sendHeartbeatRef.current?.();
      }, 500);
    }
  }, [location.pathname]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (socketRef.current && socketRef.current.connected) {
        const sessionId = getSessionId();
        socketRef.current.emit('active-user:disconnect', { session_id: sessionId });
        socketRef.current.disconnect();
      }
      
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
      }
      
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [getSessionId]);

  // This component doesn't render anything
  return null;
};

export default ActiveUserTracker;

